import React, { useEffect, useState } from 'react';
import Switch from '@mui/material/Switch';

// @mui
import { Typography, Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, IconButton, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import KanbanInputName from './kanban-input-name';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CustomModal from 'src/components/modal/customModal';
import { useBoolean } from 'src/hooks/use-boolean';

const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 32,
    height: 16,
    padding: 0,
    display: 'flex',
    '&:active': {
        '& .MuiSwitch-thumb': {
            width: 15,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            transform: 'translateX(9px)',
        },
    },
    '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
            transform: 'translateX(12px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.mode === 'dark' ? '#177ddc' : '#1890ff',
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
        width: 12,
        height: 12,
        borderRadius: 6,
        transition: theme.transitions.create(['width'], {
            duration: 200,
        }),
    },
    '& .MuiSwitch-track': {
        borderRadius: 16 / 2,
        opacity: 1,
        backgroundColor:
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.25)',
        boxSizing: 'border-box',
    },
}));


const PagesRowSection = styled('div')(() => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '15px'
}));

const ActionTitle = styled(Typography)(() => ({
    fontSize: '14px',
    fontWeight: 500,
    color: "#727374",
    width: '100%',
}));

const RouteModal = ({ openRouteModal, selectedTab, selectedType, updateTabRoute }) => {

    const [data, setData] = useState([]);

    useState(() => {
        setData(selectedTab?.[selectedType] || [])

    }, [selectedTab, selectedType])

    const actions = (
        <Button variant="contained" onClick={() => {
            openRouteModal.onFalse()
            updateTabRoute(selectedTab?.id, selectedType, data)
        }}>
            Save
        </Button>
    )

    const addNewRoute = () => {
        setData((prev) => {
            const updated = [...prev];
            updated.push("");
            return updated;
        })
    }

    const deleteRoute = (index) => {
        setData((prev) => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        })
    }

    const updateRoute = (index, value) => {
        setData((prev) => {
            const updated = [...prev];
            updated[index] = value;
            return updated;

        })
    }

    const description = (
        <>
            {data.map((route, index) => {
                return (
                    <Box key={index}>
                        <KanbanInputName
                            placeholder="Route"
                            // value={tabs[id].title}
                            value={route}
                            action={actions}
                            onChange={(e) => updateRoute(index, e.target.value)}
                        />
                        <IconButton onClick={() => deleteRoute(index)}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                )

            })}
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <IconButton sx={{ width: "30px", height: "30px" }} onClick={addNewRoute} >
                    <AddCircleIcon />
                </IconButton>
            </Box>
        </>

    )

    return (
        <CustomModal open={openRouteModal.value} title="update Route" description={description} close={() => openRouteModal.onFalse()} action={actions} />
    )
}

const STRIP_WORDS = new Set(['can', 'is', 'are', 'has', 'have', 'the', 'a', 'an', 'and', 'or', 'of', 'to', 'in', 'for', 'with', 'by']);

const titleToCamelCase = (title = '') => {
    const meaningful = title.trim().split(/\s+/).filter((w) => w && !STRIP_WORDS.has(w.toLowerCase()));
    if (!meaningful.length) return '';
    return meaningful.map((word, i) => {
        const clean = word.replace(/[^a-zA-Z0-9]/g, '');
        if (!clean) return '';
        return i === 0
            ? clean.charAt(0).toLowerCase() + clean.slice(1).toLowerCase()
            : clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
    }).join('');
};

// Only camelCase allowed — no spaces, underscores, or PascalCase
const validateKey = (value) => {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
    if (!cleaned) return '';
    return cleaned.charAt(0).toLowerCase() + cleaned.slice(1);
};

const PagePermissions = ({ tabsIds, tabs, handleTabDelete, handleTabAdd, updateTabName, updateTabKey, updatePageTitle, updateTabRoute, deletePage }) => {

    const openRouteModal = useBoolean();
    const [selectedTab, setSelectedTab] = useState(null);
    const [selectedType, setSelectedType] = useState(null);

    return (
        <>

            <PagesRowSection>

                <Box
                    sx={{ display: "flex", width: "100%", alignItems: "center", gap: "10px" }}
                >

                    <KanbanInputName
                        placeholder="Permission Name"
                        value={tabsIds.title}
                        onChange={(e) => updatePageTitle(tabsIds, e.target.value)}
                    />
                    <Button
                        variant='contained'
                        color='error'
                        size="small"
                        onClick={() => deletePage(tabsIds?.id)}
                    >
                        Delete Page
                    </Button>
                </Box>

                <TableContainer component={Paper} sx={{ borderRadius: '0px', overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 500, width: '100%', tableLayout: 'fixed' }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: '52%' }}>Name</TableCell>
                                <TableCell style={{ width: '28%' }}>
                                    Key
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: 10 }}>
                                        camelCase · used for permission checks
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" style={{ width: '7%' }}>Read</TableCell>
                                <TableCell align="center" style={{ width: '7%' }}>Write</TableCell>
                                <TableCell align="center" style={{ width: '6%' }}>Delete</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tabsIds?.child?.map((id) => (
                                tabs[id] && <TableRow
                                    key={id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell component="th" scope="row">
                                        <ActionTitle>
                                            <KanbanInputName
                                                placeholder="Permission Name"
                                                value={tabs[id].title}
                                                onChange={(e) => updateTabName(tabs[id], e.target.value)}
                                                sx={{ width: '100%' }}
                                                onBlur={() => {
                                                    const generated = validateKey(titleToCamelCase(tabs[id].title));
                                                    if (!generated) return;
                                                    updateTabKey(tabs[id], generated);
                                                }}
                                            />
                                        </ActionTitle>
                                    </TableCell>
                                    <TableCell>
                                        <KanbanInputName
                                            placeholder="enter key"
                                            value={tabs[id].key || ''}
                                            onChange={(e) => updateTabKey(tabs[id], validateKey(e.target.value))}
                                            sx={{ width: '100%' }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton onClick={() => {
                                            setSelectedTab(id);
                                            setSelectedType("r");
                                            openRouteModal.onTrue();
                                        }}>
                                            <AddCircleIcon />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton onClick={() => {
                                            setSelectedTab(id);
                                            setSelectedType("w");
                                            openRouteModal.onTrue();
                                        }}>
                                            <AddCircleIcon />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton onClick={() => handleTabDelete(tabs[id])} sx={{ color: 'error.main' }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>

                                </TableRow>
                            )

                            )}
                        </TableBody>
                        {selectedTab && selectedType && openRouteModal.value && <RouteModal openRouteModal={openRouteModal} selectedTab={tabs?.[selectedTab] || {}} selectedType={selectedType} updateTabRoute={updateTabRoute} />}
                    </Table>
                    <Box
                        sx={{
                            width: 450,
                            display: "flex",
                            justifyContent: "center"
                        }}
                    >

                        <IconButton onClick={() => handleTabAdd(tabsIds)}>
                            <AddCircleIcon />
                        </IconButton>
                    </Box>

                </TableContainer>

            </PagesRowSection >

        </>

    )
}

export default PagePermissions;